// src/App.js
import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { initOfflineDB } from './services/offline';
import ErrorBoundary, { RoleErrorBoundary, AsyncErrorBoundary } from './components/Common/ErrorBoundary';
import PrivateRoute from './components/Common/PrivateRoute';

import './App.css';

// Lazy load components for better performance
const Login = lazy(() => import('./components/Auth/Login'));
const Signup = lazy(() => import('./components/Auth/Signup'));
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const AgentDashboard = lazy(() => import('./components/Agent/AgentDashboard'));
const DoctorDashboard = lazy(() => import('./components/Doctor/DoctorDashboard'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const CHEWDashboard = lazy(() => import('./components/CHEW/CHEWDashboard'));
const SupervisorDashboard = lazy(() => import('./components/Supervisor/SupervisorDashboard'));
const OperationsLeadDashboard = lazy(() => import('./components/OperationsLead/OperationsLeadDashboard'));
const WHOHealthAssessment = lazy(() => import('./components/Common/WHOHealthAssessment'));
const MarketHomepage = lazy(() => import('./components/MarketHomepage/MarketHomepage'));
const ContactPage = lazy(() => import('./components/ContactPage/ContactPage'));
const PrivacyPolicy = lazy(() => import('./components/Legal/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./components/Legal/TermsOfUse'));
const CookiePolicy = lazy(() => import('./components/Legal/CookiePolicy'));
const FinanceDashboard = lazy(() => import('./components/Finance/FinanceDashboard'));
const AgentManagement = lazy(() => import('./components/Admin/AgentManagement'));

// NEW: Multi-service-line registration components
const UnifiedPatientForm = lazy(() => import('./components/Common/UnifiedPatientForm'));

// Loading component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column',
    gap: '20px'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #007bff',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <p style={{ color: '#666', fontSize: '16px' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function App() {
  useEffect(() => {
    // Initialize offline database with error handling
    const initDB = async () => {
      try {
        await initOfflineDB();
      } catch (error) {
        console.error('Failed to initialize offline DB:', error);
        if (error.name === 'QuotaExceededError') {
          alert('Storage quota exceeded. Please clear some browser data and refresh.');
        }
      }
    };

    initDB();

    // Check for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              if (window.confirm('A new version is available! Reload to update?')) {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                window.location.reload();
              }
            }
          });
        });
      });
    }
  }, []);

  return (
    <ErrorBoundary>
      <AsyncErrorBoundary>
        <AuthProvider>
          <Router>
            <div className="App">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {/* Public homepage */}
                  <Route path="/" element={<MarketHomepage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-use" element={<TermsOfUse />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />

                  {/* Auth routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Multi-Service-Line Registration Routes */}
                  {/* Market Health Registration */}
                  <Route path="/register-patient" element={
                    <PrivateRoute allowedRoles={['agent', 'chew', 'admin']}>
                      <UnifiedPatientForm mode="market" />
                    </PrivateRoute>
                  } />

                  {/* Outreach Event Registration */}
                  <Route path="/register-outreach" element={
                    <PrivateRoute allowedRoles={['agent', 'chew', 'admin']}>
                      <UnifiedPatientForm mode="outreach" />
                    </PrivateRoute>
                  } />

                  {/* Pop-up Clinic Registration */}
                  <Route path="/register-popup" element={
                    <PrivateRoute allowedRoles={['agent', 'chew', 'doctor', 'admin']}>
                      <UnifiedPatientForm mode="popup" />
                    </PrivateRoute>
                  } />

                  {/* Protected role-specific dashboards */}
                  <Route path="/agent" element={
                    <PrivateRoute role="agent">
                      <RoleErrorBoundary role="agent">
                        <AgentDashboard />
                      </RoleErrorBoundary>
                    </PrivateRoute>
                  } />

                  <Route path="/doctor" element={
                    <PrivateRoute role="doctor">
                      <RoleErrorBoundary role="doctor">
                        <DoctorDashboard />
                      </RoleErrorBoundary>
                    </PrivateRoute>
                  } />

                  <Route path="/chew" element={
                    <PrivateRoute role="chew">
                      <RoleErrorBoundary role="chew">
                        <CHEWDashboard />
                      </RoleErrorBoundary>
                    </PrivateRoute>
                  } />

                  <Route path="/supervisor" element={
                    <PrivateRoute role="supervisor">
                      <RoleErrorBoundary role="supervisor">
                        <SupervisorDashboard />
                      </RoleErrorBoundary>
                    </PrivateRoute>
                  } />

                  <Route path="/operations_lead" element={
                    <PrivateRoute role="operations_lead">
                      <RoleErrorBoundary role="operations_lead">
                        <OperationsLeadDashboard />
                      </RoleErrorBoundary>
                    </PrivateRoute>
                  } />

                  <Route path="/admin" element={
                    <PrivateRoute role="admin">
                      <RoleErrorBoundary role="admin">
                        <AdminDashboard />
                      </RoleErrorBoundary>
                    </PrivateRoute>
                  } />

                  <Route path="/finance_lead" element={
                    <PrivateRoute role="finance_lead">
                      <RoleErrorBoundary role="finance_lead">
                        <FinanceDashboard />
                      </RoleErrorBoundary>
                    </PrivateRoute>
                  } />

                  <Route path="/agent-management" element={
                    <PrivateRoute allowedRoles={['admin', 'finance_lead']}>
                      <AgentManagement />
                    </PrivateRoute>
                  } />

                  {/* WHO Health Assessment */}
                  <Route path="/who-assessment/:patientId" element={
                    <PrivateRoute allowedRoles={['chew', 'doctor', 'supervisor', 'admin']}>
                      <WHOHealthAssessment />
                    </PrivateRoute>
                  } />

                  {/* 404 */}
                  <Route path="*" element={
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100vh',
                      gap: '20px'
                    }}>
                      <h1 style={{ fontSize: '72px', margin: 0 }}>404</h1>
                      <p style={{ fontSize: '20px', color: '#666' }}>Page not found</p>
                      <button
                        onClick={() => window.location.href = '/'}
                        style={{
                          padding: '12px 24px',
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '16px'
                        }}
                      >
                        Go to Home
                      </button>
                    </div>
                  } />
                </Routes>
              </Suspense>
            </div>
          </Router>
        </AuthProvider>
      </AsyncErrorBoundary>
    </ErrorBoundary>
  );
}

export default App;